import { OptionType } from "../component/select";
import request from "../util/client";
import { ListParams, ListResponse } from "./types";

export interface PurchaseOrder {
    id: string;
    order_id: string;
    supplier: string;
    ordered_at: number;
    delivered_at: number;
    received_at: number;
    status: string;
    dismiss?: number | string;
}

export interface PurchaseOrderItem {
    id: string;
    order_id: string;
    sku: string;
    ordered_qty: number;
    receipt_qty: number;
    delivered_at: number | null;
    ordered_at: number;
}

export function getPurchaseOrder(params: ListParams<any>) {
    return request<ListResponse<PurchaseOrder>>('purchase_order', 'GET', params);
}

export function getPurchaseOrderItem(params: ListParams<any>) {
    return request<ListResponse<PurchaseOrderItem>>('purchase_order/item', 'GET', params);
}

export function savePurchaseOrder(params: Partial<PurchaseOrder> & { item: Array<PurchaseOrderItem> } | Array<Partial<PurchaseOrder> & { item: Array<PurchaseOrderItem> }>) {
    return request<{ data: string, msg: string }>('purchase_order', 'PUT', params);
}

export function dismissPurchaseOrder(params: { id: string, dismiss?: number | string }) {
    return request('purchase_order/dismiss', 'POST', params);
}

export function syncPurchaseOrders() {
    return request<string>('purchase_order/sync', 'POST');
}

export interface SkuQty {
    sku: string;
    qty: number;
    aq?: number;
    price?: number;
    codes?: Array<string>;
}

export interface StatData {
    date: string;
    count: number;
    total?: number;
}

export function getSoldQty(params: ListParams<any>) {
    return request<ListResponse<SkuQty>>('sales_order/qty', 'GET', params);
}

export function syncSalesOrders() {
    return request<string>('sales_order/sync', 'POST');
}

export interface SalesOrder {
    id: string;
    order_id: string;
    currency: string;
    subtotal: number;
    tax: number;
    total: number;
    status: string;
    created_at: number;
    received_at: number;
    completed_at: number;
    updated_at?: number;
    dismiss?: number | string;
}

export interface SalesOrderItem {
    id: string;
    order_id: string;
    sku: string;
    qty: number;
    unit_price: number;
    tax: number;
    rowtotal: number;
    updated_at?: string;
    comments: string;
}

export function getSalesOrder(params: ListParams<any>) {
    return request<ListResponse<SalesOrder>>('sales_order', 'GET', params);
}

export function getSalesOrderItem(params: ListParams<any>) {
    return request<ListResponse<SalesOrderItem>>('sales_order/item', 'GET', params);
}

export function saveSalesOrder(params: Partial<SalesOrder> & { item: Array<SalesOrderItem> } | Array<Partial<SalesOrder> & { item: Array<SalesOrderItem> }>) {
    return request<{ data: string, msg: string }>('sales_order', 'PUT', params);
}

export function dismissSalesOrder(params: { id: string, dismiss?: number | string }) {
    return request('sales_order/dismiss', 'POST', params);
}

export interface SalesShipment {
    id: string;
    order_id: string;
    shipment_id: string;
    customer_id: string;
    customer_name: string;
    status: string;
    updated_at?: string;
    dismiss?: number | string;
}

export interface SalesShipmentItem {
    id: string;
    shipment_id: string;
    sku: string;
    qty: number;
    updated_at?: string;
}

export function getSalesShipment(params: ListParams<any>) {
    return request<ListResponse<SalesShipment>>('sales_shipment', 'GET', params);
}

export function saveSalesShipment(params: Partial<SalesShipment> & { item: Array<SalesShipmentItem> } | Array<Partial<SalesShipment> & { item: Array<SalesShipmentItem> }>) {
    return request<{ data: string, msg: string }>('sales_shipment', 'PUT', params);
}

export function getSalesShipmentItem(params: ListParams<any>) {
    return request<ListResponse<SalesShipmentItem>>('sales_shipment/item', 'GET', params);
}

export function dismissSalesShipment(params: { id: string, dismiss?: number | string }) {
    return request('sales_shipment/dismiss', 'POST', params);
}

export function getRefundQty(params: ListParams<any>) {
    return request<ListResponse<SkuQty>>('credit_memo/qty', 'GET', params);
}

export interface CreditMemo {
    id: string;
    credit_id: string;
    currency: string;
    subtotal: number;
    tax: number;
    total: number;
    status: string;
    created_at: number;
    updated_at?: number;
    dismiss?: number | string;
}

export interface CreditMemoItem {
    id: string;
    credit_id: string;
    sku: string;
    qty: number;
    credit_price: number;
    tax: number;
    rowtotal: number;
    updated_at?: string;
    comments?: string | null;
    reason?: string | null;
}

export function getCreditMemo(params: ListParams<any>) {
    return request<ListResponse<CreditMemo>>('credit_memo', 'GET', params);
}

export function getCreditMemoItem(params: ListParams<any>) {
    return request<ListResponse<CreditMemoItem>>('credit_memo/item', 'GET', params);
}

export function saveCreditMemo(params: Partial<CreditMemo> & { item: Array<CreditMemoItem> } | Array<Partial<CreditMemo> & { item: Array<CreditMemoItem> }>) {
    return request<{ data: string, msg: string }>('credit_memo', 'PUT', params);
}

export function dismissCreditMemo(params: { id: string, dismiss?: number | string }) {
    return request('credit_memo/dismiss', 'POST', params);
}

export function syncCreditMemo() {
    return request<string>('credit_memo/sync', 'POST');
}

export function getStockQty(params: ListParams<any>) {
    return request<ListResponse<SkuQty>>('stock_keeping', 'POST', params);
}

export function syncStockQty() {
    return request<string>('stock_keeping/sync', 'POST');
}

export function adjustStock(params: { warehouse: string, stock: Array<{ sku: string, qty: number }>, reason?: string }) {
    return request<{ id: string }>('stock_keeping', 'PATCH', params);
}

export function getSalesOrderStatuses(force = false) {
    return new Promise<Array<{ status: string, count: number }>>((resolve, reject) => {
        let cached = sessionStorage.getItem('sales_order/status');
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<{ status: string, count: number }>>('sales_order/status', 'GET', { count: 1 }).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('sales_order/status', JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export function statSalesOrders(params?: { filter?: any, group?: string, timezone?: string }, force = false) {
    return new Promise<Array<Required<StatData>>>((resolve, reject) => {
        let cached = sessionStorage.getItem('sales_order/stat?' + (params?.group ?? 'm'));
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<Required<StatData>>>('sales_order/stat', 'GET', params).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('sales_order/stat?' + (params?.group ?? 'm'), JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export function getShipmentStatuses(force = false) {
    return new Promise<Array<{ status: string, count: number }>>((resolve, reject) => {
        let cached = sessionStorage.getItem('sales_shipment/status');
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<{ status: string, count: number }>>('sales_shipment/status', 'GET', { count: 1 }).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('sales_shipment/status', JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export function getPurchaseOrderStatuses(force = false) {
    return new Promise<Array<{ status: string, count: number }>>((resolve, reject) => {
        let cached = sessionStorage.getItem('purchase_order/status');
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<{ status: string, count: number }>>('purchase_order/status', 'GET', { count: 1 }).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('purchase_order/status', JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export function getCreditMemoStatuses(force = false) {
    return new Promise<Array<{ status: string, count: number }>>((resolve, reject) => {
        let cached = sessionStorage.getItem('credit_memo/status');
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<{ status: string, count: number }>>('credit_memo/status', 'GET', { count: 1 }).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('credit_memo/status', JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export function statCreditMemos(params?: { filter?: any, group?: string, timezone?: string }, force = false) {
    return new Promise<Array<Required<StatData>>>((resolve, reject) => {
        let cached = sessionStorage.getItem('credit_memo/stat?' + (params?.group ?? 'm'));
        if (!force && cached) {
            resolve(JSON.parse(cached));
            return;
        }
        request<Array<Required<StatData>>>('credit_memo/stat', 'GET', params).then(response => {
            if (typeof response === 'string') {
                reject();
            } else {
                sessionStorage.setItem('credit_memo/stat?' + (params?.group ?? 'm'), JSON.stringify(response));
                resolve(response);
            }
        }, reject);
    });
}

export interface Product {
    sku: string;
    price: number;
    width: number;
    height: number;
    depth: number;
    group: string;
}

export function getProducts(params: ListParams<any>) {
    return request<ListResponse<Product>>('product', 'POST', params);
}

export async function getSkus() {
    if (localStorage.getItem('sku')) {
        return JSON.parse(localStorage.getItem('sku') ?? '[]') as Array<OptionType>;
    } else {
        let response = await getProducts({ filter: { status: { gt: 0 } }, limit: -1 }), result: Array<OptionType> = [];
        if (typeof response !== 'string') {
            result = response.data.map(item => ({ value: item.sku, label: item.sku }));
            localStorage.setItem('sku', JSON.stringify(result));
        }
        return result;
    }
}

export function syncProducts() {
    return request<string>('product/sync', 'POST');
}

interface ProductGroup {
    package_id: number;
    code: string;
    name: string;
    status: boolean;
    sort_order: number;
}

export function getProductGroups() {
    return request<Array<string>>('product_group', 'GET');
}

export function getProductSubGroups(group?: Array<string>) {
    return request<Array<string>>('product_sub_group', 'GET', { group });
}

export function syncProductGroups() {
    return request<string>('product_group/sync', 'POST');
}

export interface Supplier {
    id: string;
    code: string;
    name: string;
    email: string;
    tax_rate: number;
    currency: string;
    updated_at?: number;
    note: string;
}

export function getSupplier(params: ListParams<any>) {
    return request<ListResponse<Supplier>>('supplier', 'GET', params);
}

export function saveSupplier(params: Partial<Supplier> | FormData) {
    return request<{ data: string, msg: string }>('supplier', 'PUT', params);
}
